import React, { useState } from 'react';
import './App.css';
import PersonalInfoForm from './components/PersonalInfoForm';
import ProfessionalSummaryForm from './components/ProfessionalSummaryForm';
import ExperienceForm from './components/ExperienceForm';
import EducationForm from './components/EducationForm';
import SkillsForm from './components/SkillsForm';
import CertificationsForm from './components/CertificationsForm';
import ResumePreview from './components/ResumePreview';
import axios from 'axios';

function App() {
  const [activeSection, setActiveSection] = useState('personal');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      portfolio: ''
    },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    certifications: []
  });

  const handlePersonalInfoChange = (data) => {
    setFormData({ ...formData, personalInfo: data });
  };

  const handleSummaryChange = (summary) => {
    setFormData({ ...formData, summary });
  };

  const handleExperienceChange = (experience) => {
    setFormData({ ...formData, experience });
  };

  const handleEducationChange = (education) => {
    setFormData({ ...formData, education });
  };

  const handleSkillsChange = (skills) => {
    setFormData({ ...formData, skills });
  };

  const handleCertificationsChange = (certifications) => {
    setFormData({ ...formData, certifications });
  };

  const generatePDF = async () => {
    if (!formData.personalInfo.fullName) {
      alert('Please enter at least your full name before generating the resume.');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post('/api/generate-resume', formData, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formData.personalInfo.fullName.replace(/\s+/g, '_')}_Resume.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: '👤' },
    { id: 'summary', label: 'Summary', icon: '📝' },
    { id: 'experience', label: 'Experience', icon: '💼' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'skills', label: 'Skills', icon: '⚡' },
    { id: 'certifications', label: 'Certifications', icon: '🏆' }
  ];

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">⚡</span>
            <h1>ATS Generator Z</h1>
          </div>
          <p className="tagline">Create Professional ATS-Optimized Resumes</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-container">
        <div className="content-wrapper">
          {/* Left Side - Form */}
          <div className="form-container">
            <div className="form-card">
              {/* Navigation Tabs */}
              <div className="section-tabs">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    className={`tab ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <span className="tab-icon">{section.icon}</span>
                    <span className="tab-label">{section.label}</span>
                  </button>
                ))}
              </div>

              {/* Form Content */}
              <div className="form-content">
                {activeSection === 'personal' && (
                  <PersonalInfoForm
                    data={formData.personalInfo}
                    onChange={handlePersonalInfoChange}
                  />
                )}
                {activeSection === 'summary' && (
                  <ProfessionalSummaryForm
                    data={formData.summary}
                    onChange={handleSummaryChange}
                  />
                )}
                {activeSection === 'experience' && (
                  <ExperienceForm
                    data={formData.experience}
                    onChange={handleExperienceChange}
                  />
                )}
                {activeSection === 'education' && (
                  <EducationForm
                    data={formData.education}
                    onChange={handleEducationChange}
                  />
                )}
                {activeSection === 'skills' && (
                  <SkillsForm
                    data={formData.skills}
                    onChange={handleSkillsChange}
                  />
                )}
                {activeSection === 'certifications' && (
                  <CertificationsForm
                    data={formData.certifications}
                    onChange={handleCertificationsChange}
                  />
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="form-navigation">
                <button
                  className="nav-button prev"
                  onClick={() => {
                    const currentIndex = sections.findIndex(s => s.id === activeSection);
                    if (currentIndex > 0) {
                      setActiveSection(sections[currentIndex - 1].id);
                    }
                  }}
                  disabled={activeSection === 'personal'}
                >
                  ← Previous
                </button>
                <button
                  className="nav-button next"
                  onClick={() => {
                    const currentIndex = sections.findIndex(s => s.id === activeSection);
                    if (currentIndex < sections.length - 1) {
                      setActiveSection(sections[currentIndex + 1].id);
                    }
                  }}
                  disabled={activeSection === 'certifications'}
                >
                  Next →
                </button>
              </div>

              {/* Download Button */}
              <button 
                className="download-button"
                onClick={generatePDF}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner"></span>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    📥 Download Resume PDF
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Side - Preview */}
          <div className="preview-container">
            <ResumePreview data={formData} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="app-footer">
        <p>© 2025 ATS Generator Z | Your Professional Resume Builder</p>
      </footer>
    </div>
  );
}

export default App;
